import TrialBanner from "./TrialBanner";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, LogOut, Crown, Menu, X, ShieldCheck, LifeBuoy, Activity, Bell, KeyRound, Settings as SettingsIcon, CheckSquare } from "lucide-react";
import SidebarNav, { NAV } from "./SidebarNav";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import HealthPopups from "./HealthPopups";
import MobileNav from "./MobileNav";
import { useTableLists } from "../lib/useTableLists";

const ADMIN_NAV = { to: "/admin", label: "Admin", icon: ShieldCheck, adminOnly: true };
const CONSOLE_NAV = [
  { to: "/console", label: "Dashboard", icon: LayoutDashboard, end: true },
  ADMIN_NAV,
  { to: "/console/salute", label: "Salute sistema", icon: Activity },
];

const PLAN_LABEL = { marketing: "Marketing", vendite: "Vendite", completo: "Ufficio completo", su_misura: "Su misura", free: "Marketing", pro: "Ufficio completo", enterprise: "Ufficio completo" };

export default function Layout() {
  const { user, logout, checkAuth } = useAuth();
  const stopImpersonation = async () => {
    try { await api.post("/admin/stop-impersonation"); await checkAuth(); window.location.href = "/admin"; }
    catch (e) { console.error("stop impersonation", e); }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [ghl, setGhl] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [modules, setModules] = useState(() => { try { return JSON.parse(localStorage.getItem("athanor-modules")) || null; } catch { return null; } });
  useTableLists();
  useEffect(() => {
    if (!user || (user.role === "admin" && !user.impersonated_by)) return;
    api.get("/settings").then(({ data }) => setModules(data.modules || null)).catch(() => {});
  }, [user?.user_id]);
  useEffect(() => { setProfileOpen(false); }, [location.pathname]);

  useEffect(() => {
    api.get("/integrations/ghl/status").then(({ data }) => setGhl(data)).catch(() => {});
    api.get("/notifications").then(({ data }) => setNotif(data)).catch(() => {});
  }, [location.pathname]);

  const openNotif = async () => {
    setNotifOpen((v) => !v);
    if (!notifOpen) {
      try {
        const { data } = await api.get("/notifications");
        setNotif(data);
      } catch (e) { console.error("Notifiche", e); }
    }
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    setNotif((n) => ({ ...n, unread: 0, items: n.items.map((i) => ({ ...i, read: true })) }));
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-bg">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-line bg-surface transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="relative flex items-center justify-center px-4 py-6">
          <img src="/athanor-logo.png" alt="Athanor Lab" data-testid="sidebar-logo" className="h-14 w-auto max-w-[200px] object-contain" />
          <button onClick={() => setSidebarOpen(false)} data-testid="sidebar-close" className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-md text-slate-500 hover:text-slate-200 lg:hidden" aria-label="Chiudi menu">
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="mt-2 flex-1 space-y-1 overflow-y-auto px-2" data-testid="sidebar-nav">
          <SidebarNav items={user?.role === "admin" && !user?.impersonated_by ? CONSOLE_NAV : NAV} onNavigate={() => setSidebarOpen(false)} user={user} />
          {user?.team_role && (
            <Link to="/password" onClick={() => setSidebarOpen(false)} data-testid="nav-password" className="nav-item flex h-11 items-center gap-3 rounded-md px-3 text-sm text-slate-400 hover:bg-surface-2 hover:text-slate-100 lg:h-8">
              <KeyRound className="h-[18px] w-[18px]" /> La mia password
            </Link>
          )}
        </nav>
        <div className="border-t border-zinc-800/80 p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-200" data-testid="user-name">{user?.name}</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Crown className="h-3 w-3" />
                <span data-testid="plan-badge">{user?.team_role ? ({ sales: "Vendite", marketing: "Marketing", secretary: "Segreteria", seller: "Venditore" }[user.team_role]) : `Piano ${PLAN_LABEL[user?.plan] || user?.plan}`}</span>
              </div>
            </div>
            <button onClick={doLogout} data-testid="logout-button" title="Esci"
              className="flex h-11 w-11 items-center justify-center rounded-md text-slate-500 hover:bg-surface-2 hover:text-slate-200 lg:h-9 lg:w-9">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/70 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}
      <div className="ml-0 flex min-h-screen min-w-0 flex-1 flex-col lg:ml-[248px]">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-line bg-bg/80 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-open" className="hidden h-11 w-11 items-center justify-center rounded-md text-slate-400 hover:bg-surface-2 md:flex lg:hidden" aria-label="Apri menu">
              <Menu className="h-5 w-5" />
            </button>
            {modules?.sales !== false && <Link to="/impostazioni?tab=ghl"
              data-testid="ghl-status-badge"
              title={ghl?.error ? `Ultima sincronizzazione con errore: ${ghl.error}` : ""}
              className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap text-[13px] text-slate-400 hover:text-slate-100 md:min-h-0"
            >
              <span className={`h-2 w-2 rounded-full ${ghl?.error ? "bg-warning" : ghl?.connected ? "bg-success" : "bg-zinc-500"}`} />
              {ghl?.error ? "GHL: errore di sincronizzazione" : ghl?.connected ? "GHL collegato" : "GHL non collegato"}
            </Link>}
          </div>
          <div className="relative flex items-center gap-2">
            <Link to="/hermes" data-testid="quick-hermes-button"
              className="btn btn-secondary whitespace-nowrap px-3 sm:px-4">
              <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">Chiedi a Hermes</span><span className="sm:hidden">Hermes</span>
            </Link>
            <button onClick={openNotif} data-testid="notifications-bell"
              className="relative flex h-11 w-11 items-center justify-center rounded-md text-slate-400 hover:bg-surface-2 hover:text-slate-100 md:h-9 md:w-9" aria-label="Notifiche">
              <Bell className="h-5 w-5" />
              {notif.unread > 0 && (
                <span data-testid="notifications-count"
                  className="absolute right-0 top-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-xs font-semibold text-black">
                  {notif.unread > 99 ? "99+" : notif.unread}
                </span>
              )}
            </button>
            <button onClick={() => { setProfileOpen((v) => !v); setNotifOpen(false); }} data-testid="profile-avatar" aria-label="Profilo"
              className="flex h-11 w-11 items-center justify-center rounded-full md:hidden">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-2 text-sm font-semibold text-slate-100">{(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}</span>
            </button>
            {profileOpen && (
              <div data-testid="profile-menu" className="absolute right-0 top-12 w-64 rounded-lg border border-line bg-surface p-2 shadow-xl shadow-black/40 md:hidden">
                <div className="px-3 py-2"><div className="truncate text-sm font-medium text-slate-100">{user?.name}</div><div className="truncate text-xs text-slate-500">{user?.email}</div></div>
                {(!user?.role || user.role !== "admin" || user.impersonated_by) && (
                  <Link to="/impostazioni" data-testid="profile-settings" className="flex h-11 items-center gap-3 rounded-md px-3 text-sm text-slate-200 hover:bg-surface-2"><SettingsIcon className="h-[18px] w-[18px] text-slate-400" /> Impostazioni</Link>
                )}
                <Link to="/processo" data-testid="profile-process" className="flex h-11 items-center gap-3 rounded-md px-3 text-sm text-slate-200 hover:bg-surface-2"><CheckSquare className="h-[18px] w-[18px] text-slate-400" /> Processi</Link>
                {user?.team_role && <Link to="/password" data-testid="profile-password" className="flex h-11 items-center gap-3 rounded-md px-3 text-sm text-slate-200 hover:bg-surface-2"><KeyRound className="h-[18px] w-[18px] text-slate-400" /> La mia password</Link>}
                <button onClick={doLogout} data-testid="profile-logout" className="flex h-11 w-full items-center gap-3 rounded-md px-3 text-sm text-slate-200 hover:bg-surface-2"><LogOut className="h-[18px] w-[18px] text-slate-400" /> Esci</button>
              </div>
            )}
            {notifOpen && (
              <div data-testid="notifications-panel"
                className="absolute right-0 top-12 w-[min(24rem,calc(100vw-2rem))] rounded-lg border border-line bg-surface shadow-xl shadow-black/40">
                <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
                  <span className="text-sm font-semibold text-slate-100">Notifiche</span>
                  <button onClick={readAll} data-testid="notifications-read-all"
                    data-inline className="text-xs text-slate-300 hover:underline">Segna tutte come lette</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notif.items.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notifications-empty">
                      Nessuna notifica <span className="text-slate-600">·</span> <Link to="/avvisi" onClick={() => setNotifOpen(false)} className="font-medium text-slate-100 hover:underline">Configura gli avvisi</Link>
                    </div>
                  ) : (
                    notif.items.map((n) => (
                      <div key={n.notif_id} data-testid={`notification-${n.notif_id}`}
                        className={`border-b border-zinc-800/60 px-4 py-3 ${n.read ? "opacity-50" : ""}`}>
                        <div className="text-sm font-medium text-slate-100">{n.title}</div>
                        <div className="mt-0.5 text-xs text-slate-400">{n.body}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </header>
        <HealthPopups />
        {!(user?.role === "admin" && !user?.impersonated_by) && <MobileNav user={user} modules={modules} />}
        <main className="mx-auto w-full min-w-0 max-w-[1320px] flex-1 overflow-x-hidden p-4 pb-[calc(88px+env(safe-area-inset-bottom))] sm:p-6 md:pb-6 lg:p-8">
          {user?.impersonated_by && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-2 text-sm text-amber-200" data-testid="impersonation-banner">
              <span>Stai vedendo l'account di <strong>{user.email}</strong> come amministratore.</span>
              <button onClick={stopImpersonation} data-testid="stop-impersonation" className="rounded-md border border-amber-400/60 px-3 py-1 text-xs font-medium text-amber-100 hover:bg-amber-500/20">Torna all'admin</button>
            </div>
          )}
          <TrialBanner user={user} />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
